import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import api_endpoint from '../settings';
import { useTranslation } from 'react-i18next';
import './TrackCommentsPanel.css';

const TrackCommentsPanel = ({ trackId, trackTitle, onClose }) => {
  const { t, i18n } = useTranslation();
  const accessToken = useSelector(state => state.Auth.accessToken);
  const me = useSelector(state => state.API.me);

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${api_endpoint}/audio/tracks/${trackId}/comments`);
      const data = await res.json();
      setComments(data.comments || []);
    } catch (e) {
      setError(t('comments-load-error'));
    } finally {
      setLoading(false);
    }
  }, [trackId, t]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = newComment.trim();
    if (!body || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${api_endpoint}/audio/tracks/${trackId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('Failed to post comment');
      const comment = await res.json();
      setComments(prev => [comment, ...prev]);
      setNewComment('');
    } catch (e) {
      setError(t('comments-post-error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('comments-just-now');
    if (diffMins < 60) return t('comments-minutes-ago', { count: diffMins });
    if (diffHours < 24) return t('comments-hours-ago', { count: diffHours });
    if (diffDays < 7) return t('comments-days-ago', { count: diffDays });
    return date.toLocaleDateString(i18n.language);
  };

  return (
    <div className="comments-panel">
      <div className="comments-panel-header">
        <div className="comments-panel-title">
          <span className="comments-panel-icon">💬</span>
          <div>
            <div className="comments-panel-heading">{t('comments-title')}</div>
            <div className="comments-panel-subheading">{trackTitle}</div>
          </div>
        </div>
        <button className="comments-panel-close" onClick={onClose} aria-label={t('close')}>✕</button>
      </div>

      <div className="comments-panel-body">
        {/* Comment input */}
        {me ? (
          <form className="comment-form" onSubmit={handleSubmit}>
            <textarea
              ref={textareaRef}
              className="comment-input"
              placeholder={t('comments-placeholder')}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={submitting}
            />
            <div className="comment-form-footer">
              <span className="comment-char-count">{newComment.length}/500</span>
              <button
                type="submit"
                className="comment-submit"
                disabled={!newComment.trim() || submitting}
              >
                {submitting ? t('comments-posting') : t('comments-post')}
              </button>
            </div>
          </form>
        ) : (
          <div className="comment-login-prompt">
            <span>{t('comments-signin')}</span>
          </div>
        )}

        {error && <div className="comment-error">{error}</div>}

        {/* Comments list */}
        <div className="comments-list">
          {loading ? (
            <div className="comments-loading">{t('comments-loading')}</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">
              <span>{t('comments-empty')}</span>
              {me && <span>{t('comments-be-first')}</span>}
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="comment-item">
                <div className="comment-avatar">{comment.username[0].toUpperCase()}</div>
                <div className="comment-content">
                  <div className="comment-meta">
                    <span className="comment-username">{comment.username}</span>
                    <span className="comment-date">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="comment-body">{comment.body}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackCommentsPanel;
